/**
 * Non-destructive reconciliation for `{ byId, allIds }` redux slices.
 *
 * Used after a SignalR reconnect to merge a fresh REST snapshot into the store
 * WITHOUT the "fresh login" effect of a full replace:
 *   - unchanged entities keep their exact previous object reference, so any
 *     connected component selecting them does not re-render (no flicker, no
 *     reset of local/form state);
 *   - changed entities are replaced with the incoming version;
 *   - new entities are added; entities absent from the snapshot are removed;
 *   - if nothing changed at all, the SAME slice reference is returned, so the
 *     whole subtree is a no-op.
 *
 * Equality is a structural (JSON) comparison. These slices hold plain
 * serialisable data of modest size, so this is both correct and cheap.
 *
 * `options.volatileKeys` lists entity fields to ignore when deciding whether an
 * entity changed — e.g. the gateways snapshot stamps a client-side
 * `currentDateTime = new Date()` on every fetch, which would otherwise make
 * every entity look changed on each reconcile.
 */
const stripVolatile = (obj, volatileKeys) => {
  if (!volatileKeys.length || !obj || typeof obj !== "object") return obj;
  const clone = { ...obj };
  for (const key of volatileKeys) delete clone[key];
  return clone;
};

export const reconcileById = (state, incoming, options = {}) => {
  if (
    !incoming ||
    typeof incoming !== "object" ||
    !incoming.byId ||
    !Array.isArray(incoming.allIds)
  ) {
    return state;
  }

  const volatileKeys = options.volatileKeys ?? [];
  const nextById = {};
  let changed = false;

  for (const id of incoming.allIds) {
    const prev = state.byId?.[id];
    const next = incoming.byId[id];
    if (
      prev !== undefined &&
      JSON.stringify(stripVolatile(prev, volatileKeys)) ===
        JSON.stringify(stripVolatile(next, volatileKeys))
    ) {
      nextById[id] = prev; // preserve reference → no re-render for this entity
    } else {
      nextById[id] = next;
      changed = true;
    }
  }

  const removed = (state.allIds ?? []).some((id) => !(id in incoming.byId));
  const allIdsEqual =
    (state.allIds?.length ?? 0) === incoming.allIds.length &&
    (state.allIds ?? []).every((id, i) => id === incoming.allIds[i]);

  if (!changed && !removed && allIdsEqual) {
    return state; // nothing changed → keep the same reference
  }

  return { ...state, byId: nextById, allIds: incoming.allIds };
};
